/**
 * Board generation.
 *
 * The board is a directed acyclic graph laid out on a column/lane grid. Columns
 * advance left to right; lanes are parallel routes within a column. Branches
 * open when a column has more lanes than the previous one and converge at each
 * segment's anchor, so the player is never stranded on a dead route.
 */

import type { Rng } from './rng';
import type { BoardModel, BoardNode, LifeStage } from './types';
import { BOARD_SEGMENTS } from './content';
import type { BoardNodeTemplate } from './content/schema';

const DEFAULT_YEAR_COST: Partial<Record<BoardNode['kind'], number>> = {
  start: 0,
  end: 0,
};

/**
 * Target age at the end of each stage's segment. Board time is *scheduled*
 * rather than accumulated: a node's `yearCost` is derived so that stepping
 * through a segment lands the player inside the matching age band. Without
 * this, a 40-column board with one year per column would reach "retirement"
 * at 58 and the stage-gated content would never fire.
 *
 * Early life gets roughly one year per decision; late life compresses several
 * years into each, which also mirrors how memory actually works.
 */
const STAGE_AGE_END: Record<LifeStage, number> = {
  launch: 22,
  earlyCareer: 28,
  establishing: 35,
  growth: 45,
  midlife: 55,
  lateCareer: 64,
  preRetirement: 68,
  retirement: 80,
  lateLife: 93,
};


function laneOffsets(count: number): number[] {
  switch (count) {
    case 1:
      return [0];
    case 2:
      return [-1, 1];
    case 3:
      return [-1, 0, 1];
    default: {
      const out: number[] = [];
      const half = Math.floor(count / 2);
      for (let i = -half; out.length < count; i++) out.push(i);
      return out;
    }
  }
}

function instantiate(
  template: BoardNodeTemplate,
  id: string,
  column: number,
  lane: number,
  stage: LifeStage,
): BoardNode {
  const node: BoardNode = {
    id,
    kind: template.kind,
    title: template.title,
    column,
    lane,
    stage,
    yearCost: template.yearCost ?? DEFAULT_YEAR_COST[template.kind] ?? 1,
    next: [],
    pools: template.pools ? [...template.pools] : [],
    actions: template.actions ? [...template.actions] : [],
    milestone: template.milestone ?? false,
  };
  if (template.subtitle) node.subtitle = template.subtitle;
  return node;
}

export function generateBoard(rng: Rng, boardId = 'board.default', startAge = 18): BoardModel {
  const nodes: Record<string, BoardNode> = {};
  const order: string[] = [];
  let column = 0;
  let previousColumnIds: string[] = [];
  let ageCursor = startAge;

  const start = instantiate(
    { kind: 'start', title: 'Start', subtitle: 'Eighteen, and unassigned', yearCost: 0, milestone: true },
    'n_start',
    column,
    0,
    'launch',
  );
  nodes[start.id] = start;
  order.push(start.id);
  previousColumnIds = [start.id];
  column += 1;

  for (const segment of BOARD_SEGMENTS) {
    const segRng = rng.keyed(`board:${segment.id}`);
    const segmentEndAge = STAGE_AGE_END[segment.stage];
    const segmentStartAge = ageCursor;
    const span = Math.max(segment.columns, segmentEndAge - segmentStartAge);

    for (let c = 0; c < segment.columns; c++) {
      const isLast = c === segment.columns - 1;
      const laneCount = isLast && segment.anchor ? 1 : (segment.lanesPerColumn[c] ?? 1);
      const offsets = laneOffsets(laneCount);
      const columnIds: string[] = [];

      // Age this column should end at.
      const targetAge = segmentStartAge + Math.round((span * (c + 1)) / segment.columns);
      const cost = Math.max(1, targetAge - ageCursor);
      ageCursor = targetAge;

      if (isLast && segment.anchor) {
        const node = instantiate(segment.anchor, `n_${segment.id}_anchor`, column, 0, segment.stage);
        node.yearCost = segment.anchor.kind === 'end' ? 0 : cost;
        nodes[node.id] = node;
        order.push(node.id);
        columnIds.push(node.id);
      } else {

        // Draw distinct templates per lane so parallel routes feel different.
        const pool = segment.pool;
        const weights = pool.map((t) => t.weight ?? 1);
        const chosen: BoardNodeTemplate[] = [];
        const used = new Set<number>();
        for (let l = 0; l < laneCount; l++) {
          let idx = -1;
          for (let attempt = 0; attempt < 8; attempt++) {
            const candidate = segRng.weightedIndex(weights);
            if (candidate < 0) break;
            if (!used.has(candidate) || attempt === 7) {
              idx = candidate;
              break;
            }
          }
          if (idx < 0) idx = l % pool.length;
          used.add(idx);
          chosen.push(pool[idx] as BoardNodeTemplate);
        }
        for (let l = 0; l < laneCount; l++) {
          const node = instantiate(
            chosen[l] as BoardNodeTemplate,
            `n_${segment.id}_${c}_${l}`,
            column,
            offsets[l] ?? 0,
            segment.stage,
          );
          node.yearCost = cost;
          nodes[node.id] = node;
          order.push(node.id);
          columnIds.push(node.id);
        }
      }

      // Wire the previous column to this one. Every previous node gets at
      // least one outgoing edge, and every new node at least one incoming.
      for (let i = 0; i < previousColumnIds.length; i++) {
        const from = nodes[previousColumnIds[i] as string] as BoardNode;
        if (columnIds.length === 1) {
          from.next.push(columnIds[0] as string);
        } else if (previousColumnIds.length === 1) {
          from.next.push(...columnIds);
        } else {
          // Map lanes proportionally, then add one crossover for variety.
          const target = Math.min(
            columnIds.length - 1,
            Math.round((i / Math.max(1, previousColumnIds.length - 1)) * (columnIds.length - 1)),
          );
          from.next.push(columnIds[target] as string);
          const cross = target === 0 ? 1 : target - 1;
          const crossId = columnIds[cross];
          if (crossId && !from.next.includes(crossId)) from.next.push(crossId);
        }
      }
      // Guarantee inbound edges.
      for (const id of columnIds) {
        const hasInbound = previousColumnIds.some((p) => (nodes[p] as BoardNode).next.includes(id));
        if (!hasInbound) {
          const donor = nodes[previousColumnIds[0] as string] as BoardNode;
          donor.next.push(id);
        }
      }

      previousColumnIds = columnIds;
      column += 1;
    }
  }

  const endId = previousColumnIds[0] as string;
  return {
    id: boardId,
    nodes,
    order,
    startId: start.id,
    endId,
    columns: column,
  };
}

/** Nodes reachable in one step from the player's position. */
export function nextNodes(board: BoardModel, nodeId: string): BoardNode[] {
  const node = board.nodes[nodeId];
  if (!node) return [];
  return node.next.map((id) => board.nodes[id]).filter((n): n is BoardNode => n !== undefined);
}

/** A best-effort remaining-distance measure, used for progress indication. */
export function boardProgress(board: BoardModel, nodeId: string): number {
  const node = board.nodes[nodeId];
  if (!node) return 0;
  return board.columns <= 1 ? 1 : node.column / (board.columns - 1);
}
